/**
 * UI slice - manages UI state (modals, drawers, notifications)
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  clampSidebarWidth,
  readStoredSidebarWidth,
  writeStoredSidebarWidth,
} from '../../../lib/sidebarBootstrap';

export interface ModalState {
  isOpen: boolean;
  type: string | null;
  data: Record<string, unknown> | null;
}

export interface DrawerState {
  isOpen: boolean;
  content: string | null;
  data: Record<string, unknown> | null;
}

export interface NotificationState {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
}

export interface UIState {
  modal: ModalState;
  drawer: DrawerState;
  notifications: NotificationState[];
  sidebarCollapsed: boolean;
  /**
   * Drag-resized sidebar width in px (issue #1604), clamped to
   * `[SIDEBAR_MIN_WIDTH, SIDEBAR_MAX_WIDTH]`. Seeded from the localStorage
   * mirror so the user's chosen width survives a reload; mirrored back on
   * every `setSidebarWidth` dispatch.
   */
  sidebarWidth: number;
}

const initialState: UIState = {
  modal: {
    isOpen: false,
    type: null,
    data: null,
  },
  drawer: {
    isOpen: false,
    content: null,
    data: null,
  },
  notifications: [],
  sidebarCollapsed: false,
  sidebarWidth: readStoredSidebarWidth(),
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    openModal: (state, action: PayloadAction<{ type: string; data?: Record<string, unknown> }>) => {
      state.modal.isOpen = true;
      state.modal.type = action.payload.type;
      state.modal.data = action.payload.data || null;
    },
    closeModal: (state) => {
      state.modal.isOpen = false;
      state.modal.type = null;
      state.modal.data = null;
    },
    openDrawer: (
      state,
      action: PayloadAction<{ content: string; data?: Record<string, unknown> }>
    ) => {
      state.drawer.isOpen = true;
      state.drawer.content = action.payload.content;
      state.drawer.data = action.payload.data || null;
    },
    closeDrawer: (state) => {
      state.drawer.isOpen = false;
      state.drawer.content = null;
      state.drawer.data = null;
    },
    addNotification: (state, action: PayloadAction<Omit<NotificationState, 'id'>>) => {
      const notification: NotificationState = {
        ...action.payload,
        id: Date.now().toString(),
      };
      state.notifications.push(notification);
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter((n) => n.id !== action.payload);
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    toggleSidebar: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed;
    },
    setSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
      state.sidebarCollapsed = action.payload;
    },
    /** Set the drag-resized sidebar width (clamped) and mirror it to localStorage. */
    setSidebarWidth: (state, action: PayloadAction<number>) => {
      state.sidebarWidth = clampSidebarWidth(action.payload);
      writeStoredSidebarWidth(state.sidebarWidth);
    },
  },
});

export const {
  openModal,
  closeModal,
  openDrawer,
  closeDrawer,
  addNotification,
  removeNotification,
  clearNotifications,
  toggleSidebar,
  setSidebarCollapsed,
  setSidebarWidth,
} = uiSlice.actions;

export default uiSlice.reducer;

// Selectors
export const selectModal = (state: { ui: UIState }): ModalState => state.ui.modal;
export const selectDrawer = (state: { ui: UIState }): DrawerState => state.ui.drawer;
export const selectNotifications = (state: { ui: UIState }): NotificationState[] =>
  state.ui.notifications;
export const selectSidebarCollapsed = (state: { ui: UIState }): boolean =>
  state.ui.sidebarCollapsed;
export const selectSidebarWidth = (state: { ui: UIState }): number => state.ui.sidebarWidth;
